import React, { useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ArrowBack from '@mui/icons-material/ArrowBack';
import CheckCircle from '@mui/icons-material/CheckCircle';
import FlashOn from '@mui/icons-material/FlashOn';
import Undo from '@mui/icons-material/Undo';
import StudentCard from '../../admin/stargarden/StudentCard';
import { hasCriterionAwardToday } from '../../admin/stargarden/criterionAwardStatus';
import CriteriaManagerDialog from '../../admin/stargarden/CriteriaManagerDialog';
import { flowerForCriterion } from '../../admin/stargarden/criterionFlower';
import PointAwardFloat from '../../admin/stargarden/PointAwardFloat';
import usePointAwardAnimation from '../../admin/stargarden/usePointAwardAnimation';

const studentProgress = (student) => ({
  stage: student.stage ?? 0,
  stageStars: student.stage_stars ?? 0,
  badgesEarned: student.badges_earned ?? 0,
  lifetimeStars: student.lifetime_stars ?? 0,
  criteriaCounts: student.criteria_counts ?? {},
  todayUsedCriteriaIds: student.today_used_criteria_ids ?? [],
});

export default function TeacherStarQuickBoard({ board, classCode, classId, onBack, onRequest, onBoardChange }) {
  const [selectedCriteriaId, setSelectedCriteriaId] = useState(null);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [lastBatchId, setLastBatchId] = useState(null);
  const [applying, setApplying] = useState(false);
  const [reverting, setReverting] = useState(false);
  const { awardAnimation, showPointAwards, clearPointAwards } = usePointAwardAnimation();
  const [snack, setSnack] = useState({ open: false, severity: 'success', message: '' });
  const [criteriaManagerOpen, setCriteriaManagerOpen] = useState(false);

  const criteria = useMemo(() => board?.criteria || [], [board?.criteria]);
  const flowerCriteria = board?.flower_criteria || criteria;
  const students = useMemo(() => (board?.students || []).map((student) => ({
    id: String(student.student_id), firstName: student.first_name, lastName: student.last_name, classId: student.class_id,
  })), [board]);
  const progressMap = useMemo(() => Object.fromEntries((board?.students || []).map((student) => [String(student.student_id), studentProgress(student)])), [board]);
  const selectedCriteria = criteria.find((criterion) => Number(criterion.id) === Number(selectedCriteriaId));
  const eligibleStudents = useMemo(() => students.filter((student) => !hasCriterionAwardToday(progressMap[student.id], selectedCriteriaId)), [students, progressMap, selectedCriteriaId]);
  const allStudentsSelected = eligibleStudents.length > 0 && eligibleStudents.every((student) => selectedStudentIds.includes(student.id));

  useEffect(() => {
    const eligibleIds = new Set(eligibleStudents.map((student) => student.id));
    setSelectedStudentIds((current) => current.every((id) => eligibleIds.has(id)) ? current : current.filter((id) => eligibleIds.has(id)));
  }, [eligibleStudents]);

  useEffect(() => {
    if (selectedCriteriaId != null && !criteria.some((criterion) => Number(criterion.id) === Number(selectedCriteriaId))) {
      setSelectedCriteriaId(null);
      setSelectedStudentIds([]);
      setLastBatchId(null);
    }
  }, [criteria, selectedCriteriaId]);

  const changeCriteria = (id) => {
    if (id === selectedCriteriaId) return;
    if (selectedStudentIds.length) setSnack({ open: true, severity: 'info', message: 'Kriter değiştiği için seçimler temizlendi.' });
    setSelectedCriteriaId(id);
    setSelectedStudentIds([]);
  };

  const applyBatch = async () => {
    if (!selectedCriteria || !selectedStudentIds.length || applying || reverting) return;
    setApplying(true);
    try {
      const result = await onRequest('stars/batch', { method: 'POST', body: JSON.stringify({ criteria_id: Number(selectedCriteriaId), student_ids: selectedStudentIds.map(Number) }) });
      onBoardChange(await onRequest('stars'));
      const updated = result.updated_students || [];
      const failed = result.failed_students || [];
      showPointAwards(updated.map((student) => String(student.student_id)));
      setLastBatchId(result.batch_id || null);
      setSelectedStudentIds([]);
      setSnack({ open: true, severity: failed.length ? 'warning' : 'success', message: failed.length ? `${updated.length} öğrenciye uygulandı, ${failed.length} öğrenciye uygulanamadı.` : `${updated.length} öğrenciye yıldız verildi!` });
    } catch (error) {
      setSnack({ open: true, severity: 'error', message: `Toplu uygulama başarısız: ${error.message}` });
    } finally {
      setApplying(false);
    }
  };

  const undoBatch = async () => {
    if (!lastBatchId || applying || reverting) return;
    setReverting(true);
    try {
      await onRequest('stars/batch/undo', { method: 'POST', body: JSON.stringify({ batch_id: lastBatchId }) });
      onBoardChange(await onRequest('stars'));
      setLastBatchId(null);
      clearPointAwards();
      setSnack({ open: true, severity: 'success', message: 'Son toplu işlem geri alındı.' });
    } catch (error) {
      setSnack({ open: true, severity: 'error', message: `Geri alma başarısız: ${error.message}` });
    } finally {
      setReverting(false);
    }
  };

  return (
    <Box sx={{ minHeight: 'calc(100dvh - 130px)', background: 'linear-gradient(180deg, #e8f5e9 0%, #c8e6c9 100%)', pb: 12, borderRadius: 3, overflow: 'hidden' }}>
      <Box sx={{ position: 'sticky', top: 0, zIndex: 6, backdropFilter: 'blur(8px)', bgcolor: 'rgba(232,245,233,0.9)', borderBottom: '1px solid rgba(46,125,50,0.15)' }}>
        <Box sx={{ px: { xs: 1.5, md: 3 }, py: 1.25, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Tooltip title="Öğretmen Moduna Dön"><IconButton onClick={onBack} aria-label="Öğretmen Moduna Dön"><ArrowBack /></IconButton></Tooltip>
          <Box sx={{ minWidth: 0 }}>
            <Typography component="h1" sx={{ fontWeight: 800, color: '#2e7d32', fontSize: 'clamp(18px, 2.4vw, 30px)' }}>⚡ Yıldız Bahçem Hızlı</Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: 'clamp(12px, 1.2vw, 14px)' }}>Önce kriteri seçin, sonra öğrenci kartlarına dokunun.</Typography>
          </Box>
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Chip label={`📚 ${classCode || 'Sınıf'}`} sx={{ fontWeight: 700, bgcolor: 'rgba(46,125,50,0.12)' }} />
            <Button variant="outlined" size="small" onClick={() => setCriteriaManagerOpen(true)} sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 700 }}>Kriter Güncelle</Button>
          </Box>
        </Box>
        <Box sx={{ px: { xs: 1.5, md: 3 }, pb: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {criteria.map((criterion) => {
            const selected = Number(selectedCriteriaId) === Number(criterion.id);
            return <Chip key={criterion.id} label={`${flowerForCriterion(flowerCriteria, criterion.id)} ${criterion.name}`} color={selected ? 'success' : 'default'} variant={selected ? 'filled' : 'outlined'} onClick={() => changeCriteria(Number(criterion.id))} sx={{ height: 38, fontSize: 14, fontWeight: selected ? 700 : 500 }} />;
          })}
        </Box>
      </Box>
      <Box sx={{ px: { xs: 1, md: 2 }, pt: 1.5 }}>
        {!selectedCriteria && <Alert severity="info" sx={{ mb: 1.5 }}>Hızlı seçim için önce bir kriter seçin.</Alert>}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))', gap: { xs: 1.5, md: 2.25 }, alignItems: 'stretch' }}>
          {students.map((student) => {
            const selected = selectedStudentIds.includes(student.id);
            const awardedToday = hasCriterionAwardToday(progressMap[student.id], selectedCriteriaId);
            return <Box key={student.id} sx={{ position: 'relative' }}>
              <StudentCard student={student} progress={progressMap[student.id]} flowerCriteria={flowerCriteria} compact selected={selected} awardedToday={awardedToday} selectable={!!selectedCriteria && !awardedToday} onClick={() => { if (selectedCriteria && !awardedToday && !applying && !reverting) setSelectedStudentIds((current) => current.includes(student.id) ? current.filter((id) => id !== student.id) : [...current, student.id]); }} animateStage={awardAnimation.studentIds.includes(student.id)} />
              {awardAnimation.studentIds.includes(student.id) && <PointAwardFloat key={`${student.id}-${awardAnimation.sequence}`} />}
              {selected && <Box sx={{ position: 'absolute', top: 8, right: 8, width: 28, height: 28, borderRadius: '50%', bgcolor: '#2e7d32', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 3 }}><CheckCircle fontSize="small" /></Box>}
            </Box>;
          })}
        </Box>
      </Box>
      <Box sx={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 7, p: { xs: 1, md: 1.5 }, bgcolor: 'rgba(255,255,255,0.96)', borderTop: '1px solid rgba(0,0,0,0.08)' }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontWeight: 700, color: '#2e7d32', mr: 'auto' }}>{selectedCriteria ? `${selectedStudentIds.length} öğrenci seçildi` : 'Kriter seçilmedi'}</Typography>
          <Chip icon={<FlashOn />} label={selectedCriteria ? `Kriter: ${selectedCriteria.name}` : 'Önce kriter seçin'} color={selectedCriteria ? 'success' : 'default'} variant={selectedCriteria ? 'filled' : 'outlined'} />
          <Button variant="outlined" disabled={!selectedCriteria || allStudentsSelected || applying || reverting || !eligibleStudents.length} onClick={() => setSelectedStudentIds(eligibleStudents.map((student) => student.id))} sx={{ textTransform: 'none', borderRadius: 3 }}>Tümünü Seç</Button>
          <Button variant="outlined" disabled={!selectedStudentIds.length || applying || reverting} onClick={() => setSelectedStudentIds([])} sx={{ textTransform: 'none', borderRadius: 3 }}>Seçimleri Kaldır</Button>
          <Button variant="outlined" startIcon={<Undo />} disabled={!lastBatchId || applying || reverting} onClick={undoBatch} sx={{ textTransform: 'none', borderRadius: 3 }}>Son toplu işlemi geri al</Button>
          <Button variant="contained" disabled={!selectedCriteria || !selectedStudentIds.length || applying || reverting} onClick={applyBatch} sx={{ textTransform: 'none', borderRadius: 3, px: 2.5, fontWeight: 700 }}>{applying ? 'Uygulanıyor...' : `${selectedStudentIds.length} öğrenciye uygula`}</Button>
        </Box>
      </Box>
      <Snackbar open={snack.open} autoHideDuration={snack.severity === 'success' ? 5000 : 3500} onClose={() => setSnack((current) => ({ ...current, open: false }))} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity={snack.severity} onClose={() => setSnack((current) => ({ ...current, open: false }))} action={snack.severity === 'success' && lastBatchId ? <Button color="inherit" size="small" onClick={undoBatch} disabled={reverting}>GERİ AL</Button> : null}>{snack.message}</Alert>
      </Snackbar>
      <CriteriaManagerDialog
        open={criteriaManagerOpen}
        onClose={() => setCriteriaManagerOpen(false)}
        scopeLabel={`${classCode || 'Bu'} sınıfı`}
        loadCriteria={() => onRequest('criteria')}
        createCriterion={(criterion) => onRequest('criteria', { method: 'POST', body: JSON.stringify(criterion) })}
        updateCriterion={(id, criterion) => onRequest(`criteria/${id}`, { method: 'PUT', body: JSON.stringify(criterion) })}
        deleteCriterion={(id) => onRequest(`criteria/${id}`, { method: 'DELETE' })}
        onChanged={async () => onBoardChange(await onRequest('stars'))}
        canManageCriterion={(criterion) => classId != null && Number(criterion.class_id) === Number(classId)}
      />
    </Box>
  );
}
